import React from 'react';
import PropTypes from 'prop-types';

import {dispatch, connect} from 'utils/state_manager';

import {Card, CardActionArea, Grid2 as Grid, Stack, Typography} from 'views/components';
import {Add as AddIcon} from 'views/icons';
import {Translate, translate} from 'views/module';

import FamilyMemberCard from './FamilyMemberCard';
import FamilyMemberDialog from './FamilyMemberDialog';
import FamilyName from './FamilyName';
import FamilyNote from './FamilyNote';

import backend from 'utils/backend';
import {toFamilyMemberBody} from 'utils/backend/loom/family/family_adapter';

const HANDLED_ERRORS = [
  'FAM_002', 'FAM_003', 'FAM_006', 'FAM_007', 'FAM_008',
  'FAM_009', 'FAM_010', 'FAM_011', 'FAM_012', 'FAM_013', 'FAM_014', 'FAM_015',
];

const errorMessage = (error) => {
  return translate(`family.FamilyMembers.Errors.${HANDLED_ERRORS.includes(error.code) ? error.code : 'Default'}`);
};

const FamilyMembers = (props) => {
  const {clientId, familyName, familyMembers, familyNotes, clientNotes, reload} = props;
  const progressWrap = props.statusProgressWrap;

  const [dialog, setDialog] = React.useState({open: false, member: null});

  const closeDialog = React.useCallback(() => setDialog({open: false, member: null}), []);

  const editMember = React.useCallback((member) => setDialog({open: true, member}), []);

  const saveMember = (form) => {
    return progressWrap(async () => {
      const body = toFamilyMemberBody(form);

      if (dialog.member) {
        await backend.loom.family.updateFamilyMember(clientId, dialog.member.familyMemberId, body);
      } else {
        await backend.loom.family.addFamilyMember(clientId, body);
      }

      closeDialog();
      reload();
    }, {
      onErrorMessage: errorMessage,
      debugString: 'FAMILY_MEMBER_SAVE',
    });
  };

  const removeMember = React.useCallback(async (member, setIsDeleting) => {
    setIsDeleting(true);
    await progressWrap(async () => {
      await backend.loom.family.removeFamilyMember(clientId, member.familyMemberId);
      reload();
    }, {
      onErrorMessage: errorMessage,
      debugString: 'FAMILY_MEMBER_REMOVE',
    });
    setIsDeleting(false);
  }, [clientId, reload]);

  return (
    <Stack spacing={2} sx={{p: 2}}>
      <FamilyName
        clientId={clientId}
        familyName={familyName}
        reload={reload}
      />
      <Grid container spacing={2}>
        <Grid size={{xs: 12, md: 6}}>
          <FamilyNote
            expression='family.FamilyMembers.Notes.FamilyNotes'
            value={familyNotes}
            save={(notes) => backend.loom.family.updateFamilyNotes(clientId, {notes})}
            reload={reload}
          />
        </Grid>
        <Grid size={{xs: 12, md: 6}}>
          <FamilyNote
            expression='family.FamilyMembers.Notes.ClientNotes'
            value={clientNotes}
            save={(notes) => backend.loom.family.updateClientNotes(clientId, {notes})}
            reload={reload}
          />
        </Grid>
      </Grid>
      {
        familyMembers.length === 0 &&
        <Typography sx={{color: 'text.secondary'}}>
          <Translate expression='family.FamilyMembers.Empty' />
        </Typography>
      }
      <Grid container spacing={2}>
        {
          familyMembers.map((member) => (
            <Grid key={member.familyMemberId} size={{xs: 12, md: 6, xl: 4}}>
              <FamilyMemberCard
                member={member}
                onEdit={editMember}
                onRemove={removeMember}
              />
            </Grid>
          ))
        }
        <Grid size={{xs: 12, md: 6, xl: 4}}>
          <Card
            variant='outlined'
            sx={{
              height: '100%',
              minHeight: '140px',
              borderRadius: '12px',
              borderStyle: 'dashed',
              borderColor: '#E8E4DE',
            }}
          >
            <CardActionArea
              onClick={() => setDialog({open: true, member: null})}
              sx={{
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 1,
                color: 'text.secondary',
              }}
            >
              <AddIcon />
              <Typography>
                <Translate expression='family.FamilyMembers.Add' />
              </Typography>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
      {
        dialog.open &&
        <FamilyMemberDialog
          member={dialog.member}
          onClose={closeDialog}
          onSave={saveMember}
        />
      }
    </Stack>
  );
};

FamilyMembers.propTypes = {
  clientId: PropTypes.string.isRequired,
  familyName: PropTypes.string,
  familyMembers: PropTypes.arrayOf(PropTypes.object).isRequired,
  familyNotes: PropTypes.string,
  clientNotes: PropTypes.string,
  reload: PropTypes.func.isRequired,
  statusProgressWrap: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
});

export default connect(mapStateToProps, dispatch)(React.memo(FamilyMembers));
